import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { compressImage } from './utils/imageCompression';
import { convertHeicFiles } from './utils/convertHeic';
import ProjectMessages from './components/ProjectMessages';
import ProjectSchedule from './components/ProjectSchedule';
import ProjectFiles from './components/ProjectFiles';
import { renderSymbolSvgChildren } from './symbols/registry';

const SharedPlanView = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [plans, setPlans] = useState([]);
  const [planNotes, setPlanNotes] = useState([]);
  const [activePlanId, setActivePlanId] = useState(null);
  const [placements, setPlacements] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newComment, setNewComment] = useState('');
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [selectedPlantForComment, setSelectedPlantForComment] = useState(null);
  const [selectedNoteForComment, setSelectedNoteForComment] = useState(null);
  const [highlightedPlantId, setHighlightedPlantId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [sheet, setSheet] = useState({ width_in: 24, height_in: 36, units: 'in', ppi: 40 });
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [activeTab, setActiveTab] = useState('notes'); // 'notes', 'messages', 'schedule', 'files'
  const [shareInfo, setShareInfo] = useState(null); // Store share info to check if user is a trade
  const [designerInfo, setDesignerInfo] = useState(null); // { id, name, email } for shared-link UI clarity
  const [composeRequest, setComposeRequest] = useState(null); // { text, nonce }
  const defaultTabSetRef = useRef(false);

  const activePlan = plans.find((p) => p.id === activePlanId) || plans[0] || null;
  const effectivePlanImageUrl = activePlan?.file_url || project?.plan_image_url || null;
  const activePlanNotes = activePlanId ? planNotes.filter((n) => n.plan_id === activePlanId) : [];

  const sheetPx = React.useMemo(() => {
    const ppi = Number(sheet?.ppi) || 40;
    const wIn = Number(sheet?.width_in) || 24;
    const hIn = Number(sheet?.height_in) || 36;
    return { w: Math.round(wIn * ppi), h: Math.round(hIn * ppi), ppi };
  }, [sheet]);
  
  // Collaboration drawer sizing (desktop)
  const COLLAPSE_WIDTH = 384; // ~w-96
  const EXPAND_WIDTH = 620;
  const MIN_WIDTH = 320;
  const MAX_WIDTH = 840;
  const WIDTH_STORAGE_KEY = 'collabDrawerWidthV1';
  const [collabDrawerWidth, setCollabDrawerWidth] = useState(() => {
    try {
      const raw = localStorage.getItem(WIDTH_STORAGE_KEY);
      const n = raw ? Number(raw) : NaN;
      if (!Number.isFinite(n)) return COLLAPSE_WIDTH;
      return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, n));
    } catch {
      return COLLAPSE_WIDTH;
    }
  });
  const resizeStateRef = useRef({ resizing: false, startX: 0, startW: COLLAPSE_WIDTH });

  useEffect(() => {
    try {
      localStorage.setItem(WIDTH_STORAGE_KEY, String(collabDrawerWidth));
    } catch {
      // ignore
    }
  }, [collabDrawerWidth]);

  useEffect(() => {
    const onMove = (e) => {
      if (!resizeStateRef.current.resizing) return;
      const x = typeof e.clientX === 'number' ? e.clientX : 0;
      const delta = resizeStateRef.current.startX - x; // drag left => larger
      const viewportMax = Math.max(MIN_WIDTH, window.innerWidth - 320);
      const max = Math.min(MAX_WIDTH, viewportMax);
      const next = Math.max(MIN_WIDTH, Math.min(max, resizeStateRef.current.startW + delta));
      setCollabDrawerWidth(next);
    };
    const onUp = () => {
      if (!resizeStateRef.current.resizing) return;
      resizeStateRef.current.resizing = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleCollabDrawerWidth = () => {
    const viewportMax = Math.max(MIN_WIDTH, window.innerWidth - 320);
    const max = Math.min(MAX_WIDTH, viewportMax);
    setCollabDrawerWidth((prev) => {
      const isExpanded = prev >= 520;
      return isExpanded ? COLLAPSE_WIDTH : Math.min(max, EXPAND_WIDTH);
    });
  };

  const isTradeViewer = (shareInfo?.share_type || '').toString().toLowerCase() === 'trade';
  const scheduleAllowed = shareInfo?.schedule_access !== false;

  const requestScheduleChangeViaChat = (kind = 'change') => {
    const header = kind === 'new' ? 'Schedule request (new item):' : 'Schedule request (change):';
    const text =
      `${header}\n` +
      `- Dates/time(s):\n` +
      `- How long onsite (each visit):\n` +
      `- What work you’ll be doing:\n` +
      `- Anything blocking you?\n`;
    // IMPORTANT: direct message to designer + this trade only (NOT "everyone")
    setComposeRequest({
      text,
      nonce: Date.now(),
      recipientsOverride: userEmail
        ? [{ type: 'trade', email: userEmail, name: userName || userEmail }]
        : null
    });
    setActiveTab('messages');
  };

  useEffect(() => {
    if (!shareInfo || defaultTabSetRef.current) return;
    defaultTabSetRef.current = true;
    setActiveTab(isTradeViewer ? 'messages' : 'notes');
  }, [shareInfo, isTradeViewer]);

  useEffect(() => {
    loadSharedProject();
  }, [token]);

  const loadSharedProject = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      
      const response = await fetch(`${API_BASE_URL}/collaboration/shared/${token}`);
      
      if (!response.ok) throw new Error('Failed to load shared project');
      const data = await response.json();
      
      setProject(data.project);
      setPlans(data.plans || []);
      setPlanNotes(data.planNotes || []);
      setPlacements(data.placements || []);
      setComments(data.comments || []);
      setUserName(data.share.recipient_name || '');
      setUserEmail(data.share.recipient_email || '');
      setShareInfo(data.share); // Store share info (includes share_type)
      setDesignerInfo(data.designer || null);

      // Sheet settings (optional; older shares may not include project_data)
      try {
        const pd = data?.project?.project_data
          ? (typeof data.project.project_data === 'string' ? JSON.parse(data.project.project_data) : data.project.project_data)
          : {};
        if (pd?.sheet) {
          const incoming = pd.sheet || {};
          setSheet({
            width_in: Number(incoming.width_in) || 24,
            height_in: Number(incoming.height_in) || 36,
            units: (incoming.units || '').toString().trim().toLowerCase() === 'cm' ? 'cm' : 'in',
            ppi: Number(incoming.ppi) || 40
          });
        }
      } catch {
        // ignore
      }

      // Select default active plan if not set
      const incomingPlans = data.plans || [];
      if (incomingPlans.length > 0) {
        setActivePlanId((prev) => prev || incomingPlans[0].id);
      } else {
        setActivePlanId(null);
      }
    } catch (error) {
      console.error('Error loading shared project:', error);
      setError('Failed to load project or link expired');
    } finally {
      setLoading(false);
    }
  };

  const handlePostComment = async (placementId = null) => {
    if (!newComment.trim() && commentAttachments.length === 0) return;
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      
      const response = await fetch(`${API_BASE_URL}/collaboration/comment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          projectId: project.id,
          shareToken: token,
          userEmail: userEmail,
          userName: userName,
          commentText: newComment,
          placementId: placementId,
          planNoteId: selectedNoteForComment?.id || null,
          attachments: commentAttachments
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to post comment');
      }
      setNewComment('');
      setCommentAttachments([]);
      setSelectedPlantForComment(null);
      setSelectedNoteForComment(null);
      setShowCommentForm(false);
      loadSharedProject(); // Reload to get new comments
    } catch (error) {
      console.error('Error posting comment:', error);
      alert(error.message || 'Failed to post comment');
    }
  };

  const handleFileSelect = async (e) => {
    let files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingAttachments(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

      files = await convertHeicFiles(files);

      const compressedFiles = await Promise.all(
        files.map(file => compressImage(file, 1920, 1920, 0.8))
      );

      const uploadPromises = compressedFiles.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('shareToken', token);

        const response = await fetch(`${API_BASE_URL}/upload/comment-attachment`, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) throw new Error('Failed to upload file');
        const data = await response.json();
        return {
          url: data.url,
          filename: data.filename || file.name,
          type: data.type || file.type,
          size: data.size || file.size
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      setCommentAttachments([...commentAttachments, ...uploadedFiles]);
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Failed to upload some files. Please try again.');
    } finally {
      setUploadingAttachments(false);
      e.target.value = ''; // Reset file input
    }
  };

  const removeAttachment = (index) => {
    setCommentAttachments(commentAttachments.filter((_, i) => i !== index));
  };

  const getPlantComments = (placementId) => {
    return comments.filter(c => c.placement_id === placementId);
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));
  const handleResetZoom = () => {
    setZoom(1);
    setPanOffset({ x: 0, y: 0 });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading shared project...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️</div>
          <p className="text-gray-900">{error || 'Project not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold text-gray-900">{project.name}</h1>
              <p className="text-sm text-gray-600">Shared landscape plan • {placements.length} plants</p>
            </div>

            <div className="flex items-center space-x-3">
              {/* Drawing selector moved into header (keeps it off the plan) */}
              {plans.length > 0 && (
                <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                  <span className="text-xs font-semibold text-gray-600">Drawing</span>
                  <select
                    value={activePlanId || ''}
                    onChange={(e) => setActivePlanId(e.target.value)}
                    className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white"
                  >
                    {plans.map((p) => (
                      <option key={p.id} value={p.id}>
                        {(p.category ? `[${p.category}] ` : '') + (p.title || 'Plan')}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Zoom Controls */}
              <div className="flex items-center space-x-2 bg-gray-100 rounded-lg px-3 py-2">
                <button onClick={handleZoomOut} className="text-gray-700 hover:text-gray-900">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                  </svg>
                </button>
                <span className="text-sm font-medium text-gray-700 w-12 text-center">
                  {Math.round(zoom * 100)}%
                </span>
                <button onClick={handleZoomIn} className="text-gray-700 hover:text-gray-900">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                </button>
                <button onClick={handleResetZoom} className="ml-2 text-sm text-gray-600 hover:text-gray-900">
                  Reset
                </button>
              </div>

              <button
                onClick={() => {
                  setShowCommentForm(!showCommentForm);
                  if (!showCommentForm) setActiveTab(isTradeViewer ? 'messages' : 'notes');
                }}
                className="inline-flex items-center px-4 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100"
              >
                <svg className="mr-2 -ml-1 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
                {showCommentForm ? 'Hide' : 'Open'} Collaboration
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex h-[calc(100vh-120px)] min-w-0">
        {/* Canvas Area */}
        <div className="flex-1 min-w-0 relative overflow-hidden bg-gray-200">
          {effectivePlanImageUrl && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                style={{
                  transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
                  transformOrigin: 'center center',
                  transition: 'transform 0.2s ease-out',
                  width: sheetPx.w,
                  height: sheetPx.h
                }}
                className="relative bg-white shadow"
              >
                <img
                  src={effectivePlanImageUrl}
                  alt={project.name}
                  className="max-w-none"
                  style={{ width: '100%', height: '100%', userSelect: 'none' }}
                  draggable="false"
                />

                {/* Plan Notes */}
                {activePlanNotes.map((note) => {
                  const count = comments.filter((c) => c.plan_note_id === note.id).length;
                  const w = Number(note.width) || 0;
                  const h = Number(note.height) || 0;
                  return (
                    <div
                      key={note.id}
                      className="absolute"
                      style={{
                        left: `${note.position_x || 0}%`,
                        top: `${note.position_y || 0}%`,
                        width: `${w > 0 ? w : 14}%`,
                        height: `${h > 0 ? h : 10}%`,
                        zIndex: selectedNoteForComment?.id === note.id ? 30 : 15,
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        if (isTradeViewer) {
                          const planLabel = activePlan?.title ? `Drawing: ${activePlan.title}` : '';
                          const noteText = (note.note_text || '').toString().trim();
                          const text =
                            `Question about a note on the plan:\n` +
                            (planLabel ? `- ${planLabel}\n` : '') +
                            (noteText ? `- Note: ${noteText}\n` : '') +
                            `\nCan you confirm what you want here?`;

                          setShowCommentForm(true);
                          setComposeRequest({ text, nonce: Date.now() });
                          setActiveTab('messages');
                          return;
                        }

                        setSelectedNoteForComment(note);
                        setSelectedPlantForComment(null);
                        setShowCommentForm(true);
                        setActiveTab('notes');
                      }}
                      title="Click to comment on this note"
                    >
                      <div className={`w-full h-full rounded-md border shadow-sm bg-yellow-50/95 backdrop-blur-sm p-2 ${
                        selectedNoteForComment?.id === note.id ? 'border-yellow-500 ring-2 ring-yellow-200' : 'border-yellow-200'
                      }`}>
                        <div className="flex items-start justify-between gap-2 h-full">
                          <div className="flex-1 min-w-0 text-sm text-gray-800 whitespace-pre-wrap overflow-hidden">
                            {note.note_text || <span className="text-gray-400 italic">Note</span>}
                          </div>
                          {count > 0 && (
                            <div className="shrink-0 text-[11px] font-semibold text-white bg-blue-600 rounded-full px-2 py-0.5">
                              {count}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Plant Markers */}
                {placements.map(placement => {
                  let markerSize = Math.max(10, Math.round(0.5 * (sheetPx.ppi || 40)));
                  let symbolType = 'circle';
                  try {
                    const notesData = placement.notes ? JSON.parse(placement.notes) : {};
                    symbolType = notesData.symbol || symbolType;
                    const n = Number(notesData.symbol_size_in);
                    const sizeIn = Number.isFinite(n) ? n : 0.5;
                    markerSize = Math.max(10, Math.round(sizeIn * (sheetPx.ppi || 40)));
                  } catch (e) {
                    // ignore
                  }
                  
                  const isHighlighted = highlightedPlantId === placement.id;
                  
                  return (
                    <div
                      key={placement.id}
                      className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                      style={{
                        left: `${placement.position_x}%`,
                        top: `${placement.position_y}%`,
                        zIndex: isHighlighted ? 20 : 10,
                      }}
                      onClick={() => {
                        if (isTradeViewer) {
                          const planLabel = activePlan?.title ? `Drawing: ${activePlan.title}` : '';
                          const plantName = placement.common_name || placement.botanical_name || 'Plant';
                          const qty = placement.quantity ? `Qty: ${placement.quantity}` : '';
                          const text =
                            `Question about a plant on the plan:\n` +
                            (planLabel ? `- ${planLabel}\n` : '') +
                            `- Plant: ${plantName}\n` +
                            (qty ? `- ${qty}\n` : '') +
                            `\nCan you confirm what you want me to do here?`;

                          setHighlightedPlantId(placement.id);
                          setShowCommentForm(true);
                          setComposeRequest({ text, nonce: Date.now() });
                          setActiveTab('messages');
                          return;
                        }

                        setSelectedPlantForComment(placement);
                        setSelectedNoteForComment(null);
                        setShowCommentForm(true);
                        setHighlightedPlantId(placement.id);
                      }}
                    >
                      <div className="relative group">
                        <div 
                          className="relative"
                          style={{
                            width: `${markerSize}px`,
                            height: `${markerSize}px`,
                          }}
                        >
                          <div className={`w-full h-full rounded-full overflow-hidden border-3 shadow-lg bg-white ${
                            isHighlighted ? 'border-blue-500 ring-4 ring-blue-300' : 'border-white'
                          }`}>
                            <div className="w-full h-full flex items-center justify-center bg-white">
                              <svg viewBox="0 0 100 100" className="w-full h-full">
                                {renderSymbolSvgChildren(symbolType)}
                              </svg>
                            </div>
                          </div>
                          
                          {/* Quantity Badge */}
                          {placement.quantity > 1 && (
                            <div 
                              className="absolute -top-1 -right-1 bg-red-500 border-2 border-white rounded-full flex items-center justify-center text-white font-bold shadow-lg"
                              style={{
                                width: `${Math.max(20, markerSize * 0.4)}px`,
                                height: `${Math.max(20, markerSize * 0.4)}px`,
                                fontSize: `${Math.max(10, markerSize * 0.25)}px`
                              }}
                            >
                              {placement.quantity}
                            </div>
                          )}
                          
                          {/* Comment Badge */}
                          {getPlantComments(placement.id).length > 0 && (
                            <div 
                              className="absolute -top-1 -left-1 bg-purple-500 border-2 border-white rounded-full flex items-center justify-center text-white font-bold shadow-lg"
                              style={{
                                width: `${Math.max(20, markerSize * 0.4)}px`,
                                height: `${Math.max(20, markerSize * 0.4)}px`,
                              }}
                            >
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                          
                          {/* Hover info */}
                          <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-black/75 text-white px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            {placement.common_name}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Collaboration Sidebar - Notes, Messages, Schedule */}
        {showCommentForm && (
          <div
            className="relative flex-shrink-0 bg-white shadow-xl border-l border-gray-200 flex flex-col max-w-full overflow-x-hidden"
            style={{ width: collabDrawerWidth }}
          >
            {/* Resize handle */}
            <div
              className="absolute left-0 top-0 h-full w-2 cursor-col-resize z-20 hover:bg-gray-200/60 active:bg-gray-300/60"
              title="Drag to resize"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                resizeStateRef.current = { resizing: true, startX: e.clientX, startW: collabDrawerWidth };
                document.body.style.cursor = 'col-resize';
                document.body.style.userSelect = 'none';
              }}
            />
            <div className="p-4 border-b border-gray-200">
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-lg font-semibold">Project Collaboration</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleCollabDrawerWidth}
                    className="px-2 py-1 text-xs font-medium border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    title={collabDrawerWidth >= 520 ? 'Collapse panel' : 'Expand panel'}
                  >
                    {collabDrawerWidth >= 520 ? 'Collapse' : 'Expand'}
                  </button>
                  <button
                    onClick={() => {
                      setShowCommentForm(false);
                      setSelectedPlantForComment(null);
                      setHighlightedPlantId(null);
                    setActiveTab(isTradeViewer ? 'messages' : 'notes');
                    }}
                    className="text-gray-400 hover:text-gray-600"
                    title="Close"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Tabs */}
              <div className="flex gap-2 border-b border-gray-200 -mx-4 px-4 pb-0 mb-3">
                {!isTradeViewer && (
                  <button
                    onClick={() => {
                      setActiveTab('notes');
                      setSelectedPlantForComment(null);
                      setHighlightedPlantId(null);
                    }}
                    className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'notes'
                        ? 'border-green-600 text-green-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    🗨️ Client Chat
                  </button>
                )}
                {isTradeViewer && (
                  <button
                    onClick={() => setActiveTab('messages')}
                    className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'messages'
                        ? 'border-green-600 text-green-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    💬 Trades Chat
                  </button>
                )}
                {isTradeViewer && scheduleAllowed && (
                  <button
                    onClick={() => setActiveTab('schedule')}
                    className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'schedule'
                        ? 'border-green-600 text-green-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    📅 Schedule
                  </button>
                )}
                <button
                  onClick={() => setActiveTab('files')}
                  className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'files'
                      ? 'border-green-600 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  📁 Files
                </button>
              </div>
            </div>
            
            {/* Tab Content - Notes */}
            {!isTradeViewer && activeTab === 'notes' && (
              <>
                {selectedPlantForComment && (
                  <div className="px-4 pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                        </svg>
                        <span className="text-sm font-medium text-purple-700">
                          Commenting on: {selectedPlantForComment.common_name}
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedPlantForComment(null);
                          setHighlightedPlantId(null);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                        title="View all comments"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

            <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ overflowWrap: 'anywhere' }}>
              {(selectedPlantForComment ? getPlantComments(selectedPlantForComment.id) : comments).length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <p>No comments yet</p>
                  <p className="text-sm mt-1">
                    {selectedPlantForComment ? `No comments about ${selectedPlantForComment.common_name}` : 'Be the first to add feedback!'}
                  </p>
                </div>
              ) : (
                (selectedPlantForComment ? getPlantComments(selectedPlantForComment.id) : comments).map((comment) => {
                  const plantInfo = comment.placement_id ? placements.find(p => p.id === comment.placement_id) : null;
                  const isClient = comment.user_email === userEmail;
                  
                  return (
                    <div 
                      key={comment.id} 
                      className={`rounded-lg p-3 ${
                        isClient ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'
                      } ${plantInfo && !selectedPlantForComment ? 'cursor-pointer hover:bg-purple-50' : ''}`}
                      onClick={() => {
                        if (plantInfo && !selectedPlantForComment) {
                          setSelectedPlantForComment(plantInfo);
                          setHighlightedPlantId(plantInfo.id);
                        }
                      }}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm text-gray-900">{comment.user_name}</p>
                            {isClient && (
                              <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">You</span>
                            )}
                            {comment.comment_type && (
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                comment.comment_type === 'designer' ? 'bg-blue-100 text-blue-700' :
                                comment.comment_type === 'client' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-orange-100 text-orange-700'
                              }`}>
                                {comment.comment_type === 'designer' ? 'Designer' :
                                 comment.comment_type === 'client' ? 'Client' : 
                                 comment.trade_type ? comment.trade_type.charAt(0).toUpperCase() + comment.trade_type.slice(1) : 'Trade'}
                              </span>
                            )}
                          </div>
                          {plantInfo && !selectedPlantForComment && (
                            <p className="text-xs text-purple-600 font-medium flex items-center gap-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                              </svg>
                              {plantInfo.common_name} • Tap to view plant
                            </p>
                          )}
                          <p className="text-xs text-gray-500">
                            {new Date(comment.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.comment_text}</p>
                      {comment.attachments && Array.isArray(comment.attachments) && comment.attachments.length > 0 && (
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          {comment.attachments.map((attachment, idx) => (
                            <div key={idx} className="relative">
                              {attachment.type && attachment.type.startsWith('image/') ? (
                                <img
                                  src={attachment.url}
                                  alt={attachment.filename || `Attachment ${idx + 1}`}
                                  className="w-full h-32 object-cover rounded border border-gray-200"
                                />
                              ) : (
                                <a
                                  href={attachment.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 p-2 bg-gray-100 rounded border border-gray-200 hover:bg-gray-200"
                                >
                                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                  </svg>
                                  <span className="text-xs text-gray-700 truncate">{attachment.filename || 'File'}</span>
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Add Comment Form */}
            <div className={`p-4 border-t border-gray-200 ${
              selectedPlantForComment ? 'bg-purple-50' : selectedNoteForComment ? 'bg-yellow-50' : 'bg-blue-50'
            }`}>
              <div className="mb-2 text-xs text-gray-600 italic">
                Use this to discuss the plan with your designer (plants, notes, questions).
              </div>
              {selectedPlantForComment && (
                <div className="mb-2 flex items-center gap-2 text-sm text-purple-700">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  <span className="font-medium">Commenting on: {selectedPlantForComment.common_name}</span>
                </div>
              )}
              {selectedNoteForComment && !selectedPlantForComment && (
                <div className="mb-2 flex items-center gap-2 text-sm text-yellow-800">
                  <span className="font-medium">Commenting on: Note</span>
                </div>
              )}
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={
                  selectedPlantForComment
                    ? `Comment about ${selectedPlantForComment.common_name}...`
                    : selectedNoteForComment
                      ? 'Ask a question about this note...'
                      : 'Add your feedback or questions about the design...'
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
              />
                
                {/* Add Images Button */}
                <div className="mt-2 flex items-center gap-2">
                  <label className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
                    <input
                      type="file"
                      multiple
                      accept="image/*,image/heic,image/heif,.heic,.heif"
                      onChange={handleFileSelect}
                      className="hidden"
                      disabled={uploadingAttachments}
                    />
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {uploadingAttachments ? 'Uploading...' : 'Add Images'}
                  </label>
                </div>
                
                {/* Attachments Preview */}
                {commentAttachments.length > 0 && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {commentAttachments.map((attachment, idx) => (
                      <div key={idx} className="relative">
                        <img
                          src={attachment.url}
                          alt={attachment.filename || `Preview ${idx + 1}`}
                          className="w-full h-24 object-cover rounded border border-gray-200"
                        />
                        <button
                          onClick={() => removeAttachment(idx)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
              <div className="mt-2 flex justify-end">
                <button
                  onClick={() => handlePostComment(selectedPlantForComment?.id || null)}
                    disabled={(!newComment.trim() && commentAttachments.length === 0) || uploadingAttachments}
                    className={`px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium ${
                      selectedPlantForComment ? 'bg-purple-600' : selectedNoteForComment ? 'bg-yellow-600' : 'bg-blue-600'
                    }`}
                  >
                    Post {selectedPlantForComment ? 'Plant' : selectedNoteForComment ? 'Note' : ''} Feedback
                </button>
              </div>
            </div>
          </>
          )}
          
          {/* Tab Content - Messages */}
          {activeTab === 'messages' && (
            <div className="flex-1 overflow-hidden">
              <ProjectMessages
                projectId={project.id}
                shareToken={token}
                composeRequest={composeRequest}
                viewerType={isTradeViewer ? 'trade' : 'client'}
                viewerEmail={userEmail}
                viewerName={userName}
                projectLeadName={designerInfo?.name || null}
              />
            </div>
          )}
          
          {/* Tab Content - Schedule */}
          {isTradeViewer && activeTab === 'schedule' && (
            <div className="flex-1 overflow-hidden">
              {isTradeViewer && (
                <div className="px-4 py-3 border-b border-gray-200 bg-orange-50">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs text-orange-900">
                      Trades: request schedule updates through Project Chat so the designer can approve.
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => requestScheduleChangeViaChat('new')}
                        className="px-3 py-2 bg-white border border-orange-300 text-orange-700 rounded-lg text-xs font-semibold hover:bg-orange-100 min-h-[36px]"
                      >
                        Request new date
                      </button>
                      <button
                        type="button"
                        onClick={() => requestScheduleChangeViaChat('change')}
                        className="px-3 py-2 bg-orange-600 text-white rounded-lg text-xs font-semibold hover:bg-orange-700 min-h-[36px]"
                      >
                        Request change
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <ProjectSchedule projectId={project.id} shareToken={token} />
            </div>
          )}

          {/* Tab Content - Files (shared, view-only) */}
          {activeTab === 'files' && (
            <div className="flex-1 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <p className="text-xs text-gray-700">
                  <span className="font-medium">Shared Files</span>: plan PDF, plant list, care sheets, invoices, etc.
                </p>
              </div>
              <div className="p-4 overflow-y-auto">
                <ProjectFiles projectId={project.id} shareToken={token} canUpload={false} />
              </div>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default SharedPlanView;
